import { EmailMessage } from "./email.service";

export const formatEmailDate = (dateString: string): string => {
  if (!dateString) return '';

  const date = new Date(dateString);
  const now = new Date();

  const isToday = date.toDateString() === now.toDateString();

  if (isToday) {

    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  } else {

    const day = date.getDate();
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    return `${day} ${month}`;
  }
};

export const formatFullEmailDate = (dateString: string): string => {
  if (!dateString) return '';

  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }) + ', ' + date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

export const formatDetailedEmailDate = (dateString: string): string => {
  if (!dateString) return '';

  const date = new Date(dateString);
  const now = new Date();

  const diffTime = Math.abs(now.getTime() - date.getTime());
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
  const diffMinutes = Math.floor(diffTime / (1000 * 60));

  let relativeTime = '';

  if (diffMinutes < 1) {
    relativeTime = 'now';
  } else if (diffMinutes < 60) {
    relativeTime = `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
  } else if (diffHours < 24) {
    relativeTime = `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  } else if (diffDays === 1) {
    relativeTime = 'yesterday';
  } else if (diffDays < 7) {
    relativeTime = `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  } else if (diffDays < 30) {
    const diffWeeks = Math.floor(diffDays / 7);
    relativeTime = `${diffWeeks} week${diffWeeks === 1 ? '' : 's'} ago`;
  } else if (diffDays < 365) {
    const diffMonths = Math.floor(diffDays / 30);
    relativeTime = `${diffMonths} month${diffMonths === 1 ? '' : 's'} ago`;
  } else {
    const diffYears = Math.floor(diffDays / 365);
    relativeTime = `${diffYears} year${diffYears === 1 ? '' : 's'} ago`;
  }

  const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'short' });
  const day = date.getDate();
  const month = date.toLocaleDateString('en-US', { month: 'short' });
  const time = date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });

  return `${dayOfWeek} ${day} ${month}, ${time} (${relativeTime})`;
};

export interface FileAttachment {
  url: string;
  name: string;
  type: 'pdf' | 'doc' | 'docx' | 'xls' | 'xlsx' | 'ppt' | 'pptx' | 'image' | 'video' | 'audio' | 'archive' | 'text' | 'code' | 'other';
  icon: string;
  color: string;
  size?: string;
  extension?: string;
}

export const extractFileAttachments = (content: string): FileAttachment[] => {
  if (!content) return [];

  const fileAttachments: FileAttachment[] = [];

  const linkRegex = /<a[^>]+href=["']([^"']+)["'][^>]*>([^<]+)<\/a>/gi;
  const urlRegex = /https?:\/\/[^\s<>"']+/gi;

  let match;
  while ((match = linkRegex.exec(content)) !== null) {
    const url = match[1];
    const linkText = match[2];
    const fileInfo = getFileInfo(url, linkText);
    if (fileInfo && isAllowedFileType(fileInfo.type)) {
      fileAttachments.push(fileInfo);
    }
  }

  const urlMatches = content.match(urlRegex);
  if (urlMatches) {
    urlMatches.forEach(url => {
      const fileInfo = getFileInfo(url, '');
      if (fileInfo && isAllowedFileType(fileInfo.type)) {
        fileAttachments.push(fileInfo);
      }
    });
  }

  return fileAttachments;
};

const isAllowedFileType = (type: string): boolean => {

  const allowedTypes = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'];
  return allowedTypes.includes(type);
};

const getFileInfo = (url: string, linkText: string): FileAttachment | null => {
  const lowerUrl = url.toLowerCase();
  const fileName = linkText || extractFileNameFromUrl(url);
  const extension = getFileExtension(fileName);

  if (lowerUrl.includes('docs.google.com')) {
    return {
      url,
      name: fileName || 'Google Document',
      type: 'doc',
      icon: 'fab fa-google-drive',
      color: '#4285F4',
      extension: 'DOC'
    };
  }

  if (lowerUrl.includes('sheets.google.com')) {
    return {
      url,
      name: fileName || 'Google Sheet',
      type: 'xls',
      icon: 'fas fa-chart-bar',
      color: '#0F9D58',
      extension: 'XLS'
    };
  }

  if (lowerUrl.includes('slides.google.com')) {
    return {
      url,
      name: fileName || 'Google Presentation',
      type: 'ppt',
      icon: 'fas fa-presentation',
      color: '#DB4437',
      extension: 'PPT'
    };
  }

  if (lowerUrl.includes('.pdf') || lowerUrl.includes('pdf')) {
    return {
      url,
      name: fileName || 'PDF Document',
      type: 'pdf',
      icon: 'far fa-file-pdf',
      color: '#DC3545',
      extension: 'PDF'
    };
  }

  if (lowerUrl.includes('.doc') || lowerUrl.includes('.docx')) {
    return {
      url,
      name: fileName || 'Word Document',
      type: 'doc',
      icon: 'far fa-file-word',
      color: '#824EEB',
      extension: extension?.toUpperCase() || 'DOC'
    };
  }

  if (lowerUrl.includes('.xls') || lowerUrl.includes('.xlsx')) {
    return {
      url,
      name: fileName || 'Excel Spreadsheet',
      type: 'xls',
      icon: 'far fa-file-excel',
      color: '#198754',
      extension: extension?.toUpperCase() || 'XLS'
    };
  }

  if (lowerUrl.includes('.ppt') || lowerUrl.includes('.pptx')) {
    return {
      url,
      name: fileName || 'PowerPoint Presentation',
      type: 'ppt',
      icon: 'far fa-file-powerpoint',
      color: '#DC3545',
      extension: extension?.toUpperCase() || 'PPT'
    };
  }

  if (lowerUrl.includes('.jpg') || lowerUrl.includes('.jpeg') || lowerUrl.includes('.png') || lowerUrl.includes('.gif') || lowerUrl.includes('.webp') || lowerUrl.includes('.svg')) {
    return {
      url,
      name: fileName || 'Image',
      type: 'image',
      icon: 'far fa-file-image',
      color: '#6F42C1',
      extension: extension?.toUpperCase() || 'IMG'
    };
  }

  if (lowerUrl.includes('.mp4') || lowerUrl.includes('.avi') || lowerUrl.includes('.mov') || lowerUrl.includes('.wmv') || lowerUrl.includes('.mkv') || lowerUrl.includes('.flv')) {
    return {
      url,
      name: fileName || 'Video',
      type: 'video',
      icon: 'far fa-file-video',
      color: '#FD7E14',
      extension: extension?.toUpperCase() || 'VID'
    };
  }

  if (lowerUrl.includes('.mp3') || lowerUrl.includes('.wav') || lowerUrl.includes('.flac') || lowerUrl.includes('.aac') || lowerUrl.includes('.ogg')) {
    return {
      url,
      name: fileName || 'Audio',
      type: 'audio',
      icon: 'far fa-file-audio',
      color: '#20C997',
      extension: extension?.toUpperCase() || 'AUD'
    };
  }

  if (lowerUrl.includes('.zip') || lowerUrl.includes('.rar') || lowerUrl.includes('.7z') || lowerUrl.includes('.tar') || lowerUrl.includes('.gz')) {
    return {
      url,
      name: fileName || 'Archive',
      type: 'archive',
      icon: 'far fa-file-archive',
      color: '#6C757D',
      extension: extension?.toUpperCase() || 'ZIP'
    };
  }

  if (lowerUrl.includes('.txt') || lowerUrl.includes('.md') || lowerUrl.includes('.rtf')) {
    return {
      url,
      name: fileName || 'Text Document',
      type: 'text',
      icon: 'far fa-file-alt',
      color: '#495057',
      extension: extension?.toUpperCase() || 'TXT'
    };
  }

  if (lowerUrl.includes('.js') || lowerUrl.includes('.ts') || lowerUrl.includes('.html') || lowerUrl.includes('.css') || lowerUrl.includes('.php') || lowerUrl.includes('.py') || lowerUrl.includes('.java') || lowerUrl.includes('.cpp') || lowerUrl.includes('.c')) {
    return {
      url,
      name: fileName || 'Code File',
      type: 'code',
      icon: 'fas fa-code',
      color: '#6F42C1',
      extension: extension?.toUpperCase() || 'CODE'
    };
  }

  return null;
};

const extractFileNameFromUrl = (url: string): string => {
  try {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;
    const fileName = pathname.split('/').pop();
    return fileName || 'File';
  } catch {
    return 'File';
  }
};

const getFileExtension = (fileName: string): string | null => {
  if (!fileName) return null;
  const parts = fileName.split('.');
  return parts.length > 1 ? parts[parts.length - 1] : null;
};

export const extractSenderInfo = (sendFrom: string): { name: string; email: string } => {
  if (!sendFrom) return { name: '', email: '' };

  const match = sendFrom.match(/^"?([^"<]+)"?\s*<?([^>]*)>?$/);
  if (match) {
    const name = match[1].trim();
    const email = match[2].trim();
    return { name, email };
  }

  return { name: sendFrom, email: sendFrom };
};

export const extractRecipientInfo = (sendTo: string): { name: string; email: string } => {
  if (!sendTo || typeof sendTo !== 'string') {
    return {
      name: 'Unknown Recipient',
      email: 'unknown@example.com'
    };
  }

  const emails = sendTo.split(',').map(email => email.trim());
  const firstEmail = emails[0];

  const match = firstEmail.match(/^(.+?)\s*<(.+?)>$/);
  if (match) {
    return {
      name: match[1].trim(),
      email: match[2].trim()
    };
  }

  if (firstEmail.includes('@')) {
    return {
      name: firstEmail,
      email: firstEmail
    };
  }

  return {
    name: firstEmail,
    email: firstEmail
  };
};

export const hasAttachments = (email: any): boolean => {
  if (!email) return false;

  const hasAttachmentsData = email.attachments && Array.isArray(email.attachments) && email.attachments.length > 0;

  return hasAttachmentsData;
};

export const getEmailProviderTag = (mailProviderKey: string): { label: string; color: string; icon: string; bgColor: string } => {
  switch (mailProviderKey?.toLowerCase()) {
    case 'google':
    case 'gmail':
      return {
        label: 'Gmail',
        color: '#FFFFFF',
        bgColor: '#EA4335',
        icon: 'fab fa-google'
      };
    case 'microsoft_365':
    case 'outlook':
    case 'hotmail':
      return {
        label: 'Outlook',
        color: '#FFFFFF',
        bgColor: '#0078D4',
        icon: 'fa-regular fa-envelope'
      };
    case 'yahoo':
      return {
        label: 'Yahoo',
        color: '#FFFFFF',
        bgColor: '#720E9E',
        icon: 'fab fa-yahoo'
      };
    case 'icloud':
    case 'apple':
      return {
        label: 'iCloud',
        color: '#FFFFFF',
        bgColor: '#000000',
        icon: 'fab fa-apple'
      };
    default:
      return {
        label: mailProviderKey || 'Email',
        color: '#FFFFFF',
        bgColor: '#6C757D',
        icon: 'fas fa-envelope'
      };
  }
};

export const getCrmEntityTag = (crmEntityKey: string): { icon: string; bgColor: string; color: string } => {
  const key = crmEntityKey?.toUpperCase();
  let icon = "ti ti-tag";
  switch (key) {
    case "LEAD": icon = "ti ti-chart-arcs"; break;
    case "PIPELINE": icon = "ti ti-timeline-event-exclamation"; break;
    case "DEAL": icon = "ti ti-medal"; break;
    case "CONTACT": icon = "ti ti-user-up"; break;
    case "COMPANY": icon = "ti ti-building-community"; break;
    case "PROPOSAL": icon = "ti ti-file-text"; break;
    case "CONTRACT": icon = "ti ti-file-certificate"; break;
  }
  return {
    icon,
    bgColor: "#824EEB",
    color: "#FFFFFF"
  };
};

export const getCrmEntityTooltip = (entityName: string, tabType?: string): string => {
  const type = tabType?.toLowerCase() || 'inbox';
  switch (type) {
    case 'sent':
      return `Email was sent when contact of this email was in ${entityName}`;
    case 'trash':
      return `Email was deleted when contact of this email was in ${entityName}`;
    case 'draft':
      return `Email was drafted when contact of this email was in ${entityName}`;
    case 'spam':
      return `Email was marked as spam when contact of this email was in ${entityName}`;
    case 'inbox':
    default:
      return `Email was received when contact of this email was in ${entityName}`;
  }
};